import React, { useState, useEffect } from 'react';
import { User, Order, TopUpRequest, Transaction } from '../types';
import { api } from '../services/api';
import { Button, Card, Input, StatusBadge } from './ui';
import { Plus, Upload, CheckCircle, X, Receipt, CreditCard, AlertCircle, FileText, TrendingUp, TrendingDown, Wallet, Clock, DollarSign, ArrowUpRight, Sparkles, MessageCircle } from 'lucide-react';
import { InvoiceView } from './InvoiceView';

interface FinancialsProps {
    user: User;
    orders: Order[];
    transactions: Transaction[];
    myTopUpRequests: TopUpRequest[];
    onTopUpRequest: (amount: number, file: File, orderId?: string) => Promise<void>;
}

export const Financials: React.FC<FinancialsProps> = ({ user, orders, transactions, myTopUpRequests, onTopUpRequest }) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'unpaid' | 'payment' | 'history'>('overview');
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [paymentAmount, setPaymentAmount] = useState<string>('');
    const [paymentFile, setPaymentFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [viewInvoice, setViewInvoice] = useState<Transaction | null>(null);

    // Derived State
    const unpaidOrders = orders.filter(o => !o.isPaid && o.status !== 'CANCELLED');
    const totalUnpaid = unpaidOrders.reduce((sum, o) => sum + (o.price || 0), 0);
    const totalBalance = user.balance;

    // Handle Payment Submission
    const handlePaymentSubmit = async () => {
        if (!paymentFile || !paymentAmount) return;
        setLoading(true);
        try {
            await onTopUpRequest(parseFloat(paymentAmount), paymentFile, selectedOrder?.id);
            setPaymentAmount('');
            setPaymentFile(null);
            setSelectedOrder(null);
            alert("Payment request submitted for approval.");
            setActiveTab('history');
        } catch (e) {
            console.error(e);
            alert("Failed to submit payment. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Specific Case Payment Click
    const handlePayCase = (order: Order) => {
        setSelectedOrder(order);
        setPaymentAmount(order.price.toString());
        setActiveTab('payment');
    };

    return (
        <div className="space-y-8 animate-fade-in">
            {viewInvoice && (
                <InvoiceView
                    transaction={viewInvoice}
                    user={user}
                    onClose={() => setViewInvoice(null)}
                />
            )}

            {/* Enhanced Header with Gradient Balance Card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Premium Balance Card */}
                <Card className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 border-none text-white lg:col-span-1 shadow-2xl shadow-blue-500/20">
                    {/* Animated background pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '40px 40px'
                        }}></div>
                    </div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                                <Wallet size={20} className="text-blue-200" />
                            </div>
                            <p className="text-blue-200 text-sm font-bold uppercase tracking-wide">Current Balance</p>
                        </div>

                        <div className="mb-6">
                            <h2 className={`text-5xl font-bold font-mono tracking-tight ${user.balance < 0 ? 'text-red-300' : 'text-green-300'}`}>
                                {user.balance.toFixed(2)}
                            </h2>
                            <p className="text-blue-200 text-sm mt-1 flex items-center gap-1">
                                EGP
                                {user.balance >= 0 ? (
                                    <TrendingUp size={14} className="text-green-300" />
                                ) : (
                                    <TrendingDown size={14} className="text-red-300" />
                                )}
                            </p>
                        </div>

                        <div className="space-y-3 pt-6 border-t border-white/20">
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                                <span className="text-sm text-blue-100 flex items-center gap-2">
                                    <Clock size={14} />
                                    Unpaid Cases
                                </span>
                                <span className="font-bold text-lg">{unpaidOrders.length}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-lg backdrop-blur-sm border border-red-400/20">
                                <span className="text-sm text-red-100 flex items-center gap-2">
                                    <AlertCircle size={14} />
                                    Outstanding Debt
                                </span>
                                <span className="font-bold text-red-200 text-lg">{totalUnpaid.toFixed(2)} EGP</span>
                            </div>
                        </div>
                    </div>

                    {/* Decorative gradient orb */}
                    <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl"></div>
                </Card>

                {/* Enhanced Navigation Tabs */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    <div className="flex gap-3 p-2 bg-zinc-900/80 backdrop-blur-xl rounded-2xl overflow-x-auto border border-zinc-800 shadow-xl">
                        {[
                            { id: 'overview', label: 'Overview', icon: TrendingUp },
                            { id: 'unpaid', label: 'Unpaid Cases', icon: AlertCircle, badge: unpaidOrders.length },
                            { id: 'payment', label: 'Make Payment', icon: CreditCard },
                            { id: 'history', label: 'History', icon: Receipt }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    if (tab.id === 'payment') {
                                        setSelectedOrder(null);
                                        setPaymentAmount('');
                                    }
                                    setActiveTab(tab.id as any);
                                }}
                                className={`
                                    px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 whitespace-nowrap flex items-center gap-2
                                    ${activeTab === tab.id
                                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/50 scale-105'
                                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                                    }
                                `}
                            >
                                <tab.icon size={16} />
                                {tab.label}
                                {tab.badge && tab.badge > 0 && (
                                    <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold animate-pulse">
                                        {tab.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Premium Support Card */}
                    <Card className="flex-1 flex items-center justify-center border-2 border-dashed border-zinc-700/50 bg-gradient-to-br from-zinc-900/50 to-zinc-800/30 backdrop-blur-sm hover:border-green-500/30 transition-colors duration-300">
                        <div className="text-center space-y-3">
                            <div className="inline-flex items-center gap-2 mb-2">
                                <Sparkles size={16} className="text-blue-400" />
                                <p className="text-sm font-semibold text-zinc-300">Need help with billing?</p>
                            </div>
                            <p className="text-xs text-zinc-500">
                                Contact support at{' '}
                                <span className="text-blue-400 font-mono font-bold bg-blue-500/10 px-2 py-1 rounded">
                                    01556761102
                                </span>
                            </p>
                            <Button
                                size="sm"
                                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white shadow-lg shadow-green-500/20 w-full"
                                onClick={() => window.open('https://wa.me/201556761102?text=Hello, I need help with billing.', '_blank')}
                            >
                                <MessageCircle size={16} className="mr-2" />
                                Contact on WhatsApp
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="min-h-[500px]">

                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-blue-500/10 rounded-lg">
                                    <TrendingUp size={18} className="text-blue-400" />
                                </div>
                                <h3 className="font-bold text-white text-lg">Recent Activity</h3>
                            </div>

                            <Card className="bg-zinc-900/80 border-zinc-800 shadow-xl overflow-hidden backdrop-blur-sm">
                                <div className="divide-y divide-zinc-800">
                                    {transactions.slice(0, 5).map(t => (
                                        <div
                                            key={t.id}
                                            className="p-4 hover:bg-zinc-800/50 transition-all duration-200 cursor-pointer group"
                                            onClick={() => setViewInvoice(t)}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${t.amount > 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                                        {t.amount > 0 ? (
                                                            <ArrowUpRight size={16} className="text-green-400" />
                                                        ) : (
                                                            <DollarSign size={16} className="text-red-400" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="text-white font-semibold text-sm group-hover:text-blue-400 transition-colors">
                                                            {t.description}
                                                        </div>
                                                        <div className="text-xs text-zinc-500">
                                                            {new Date(t.createdAt).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={`font-mono font-bold text-lg ${t.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {t.amount > 0 ? '+' : ''}{t.amount}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {transactions.length === 0 && (
                                        <div className="p-12 text-center text-zinc-500">
                                            <Receipt size={40} className="mx-auto mb-3 opacity-30" />
                                            <p>No recent transactions</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            <Button
                                variant="secondary"
                                className="w-full bg-zinc-800 hover:bg-zinc-700 border-zinc-700"
                                onClick={() => setActiveTab('history')}
                            >
                                View Full History
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-red-500/10 rounded-lg">
                                    <AlertCircle size={18} className="text-red-400" />
                                </div>
                                <h3 className="font-bold text-white text-lg">Pending Payments</h3>
                            </div>

                            <Card className="bg-zinc-900/80 border-zinc-800 shadow-xl overflow-hidden backdrop-blur-sm">
                                <div className="divide-y divide-zinc-800">
                                    {unpaidOrders.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).slice(0, 5).map(o => (
                                        <div key={o.id} className="p-4 hover:bg-zinc-800/50 transition-all duration-200 group">
                                            <div className="flex justify-between items-center gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white font-semibold text-sm mb-1 truncate">
                                                        {o.patientName}
                                                    </div>
                                                    <div className="text-xs text-zinc-500 flex items-center gap-2">
                                                        <Clock size={12} />
                                                        {new Date(o.createdAt).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })}
                                                        <span className="text-zinc-600">•</span>
                                                        {o.items.length} items
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono font-bold text-red-400 text-lg">
                                                        {o.price} EGP
                                                    </span>
                                                    <Button
                                                        size="sm"
                                                        className="bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-500/20"
                                                        onClick={() => handlePayCase(o)}
                                                    >
                                                        Pay Now
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {unpaidOrders.length === 0 && (
                                        <div className="p-12 text-center text-green-500/70">
                                            <CheckCircle size={40} className="mx-auto mb-3" />
                                            <p className="font-semibold">All caught up!</p>
                                            <p className="text-sm text-zinc-500 mt-1">No unpaid cases</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {unpaidOrders.length > 0 && (
                                <Button
                                    variant="secondary"
                                    className="w-full bg-red-500/10 hover:bg-red-500/20 border-red-500/30 text-red-400"
                                    onClick={() => setActiveTab('unpaid')}
                                >
                                    View All Unpaid Cases ({unpaidOrders.length})
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* UNPAID CASES TAB */}
                {activeTab === 'unpaid' && (
                    <Card className="bg-zinc-900/80 border-zinc-800 shadow-xl backdrop-blur-sm">
                        <div className="flex justify-between items-center mb-6 pb-6 border-b border-zinc-800">
                            <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                                <div className="p-2 bg-red-500/10 rounded-lg">
                                    <AlertCircle className="text-red-400" size={24} />
                                </div>
                                Unpaid Cases
                            </h3>
                            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2">
                                <DollarSign size={16} />
                                Total Due: {totalUnpaid.toFixed(2)} EGP
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-zinc-800/50 text-zinc-400 text-xs uppercase font-bold">
                                    <tr>
                                        <th className="p-4 rounded-tl-lg">Date</th>
                                        <th className="p-4">Patient</th>
                                        <th className="p-4">Doctor</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4 text-right">Amount</th>
                                        <th className="p-4 text-right rounded-tr-lg">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-800">
                                    {unpaidOrders.map(o => (
                                        <tr key={o.id} className="hover:bg-zinc-800/30 transition-colors group">
                                            <td className="p-4 text-zinc-500">{new Date(o.createdAt).toLocaleDateString()}</td>
                                            <td className="p-4 font-semibold text-white">{o.patientName}</td>
                                            <td className="p-4 text-zinc-400">{o.doctorName}</td>
                                            <td className="p-4"><StatusBadge status={o.status} /></td>
                                            <td className="p-4 text-right font-mono font-bold text-white text-lg">{o.price} EGP</td>
                                            <td className="p-4 text-right">
                                                <Button
                                                    size="sm"
                                                    className="bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-500/20"
                                                    onClick={() => handlePayCase(o)}
                                                >
                                                    Pay Now
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {unpaidOrders.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="p-16 text-center text-zinc-500">
                                                <CheckCircle size={48} className="mx-auto mb-4 text-green-500/50" />
                                                <p className="text-lg font-semibold text-green-500/70">No unpaid cases found</p>
                                                <p className="text-sm mt-2">Great job keeping up with payments!</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* MAKE PAYMENT TAB - Enhanced Design */}
                {activeTab === 'payment' && (
                    <div className="max-w-3xl mx-auto">
                        <Card className="bg-zinc-900/80 border-zinc-800 shadow-2xl backdrop-blur-sm">
                            <div className="mb-8 pb-6 border-b border-zinc-800">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-3 bg-blue-500/10 rounded-xl">
                                        <CreditCard size={24} className="text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-white">Make a Payment</h2>
                                        <p className="text-zinc-500 text-sm mt-1">Upload your payment receipt to process your request</p>
                                    </div>
                                </div>
                            </div>

                            {selectedOrder && (
                                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 p-5 rounded-2xl mb-8 animate-fade-in">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 bg-blue-500/20 text-blue-400 rounded-xl">
                                            <FileText size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-white text-lg mb-1">
                                                Payment for: {selectedOrder.patientName}
                                            </h4>
                                            <p className="text-sm text-blue-300/80 mb-3">
                                                Case ID: {selectedOrder.id.substring(0, 8)} • Amount Due: <span className="font-mono font-bold">{selectedOrder.price} EGP</span>
                                            </p>
                                            <button
                                                onClick={() => { setSelectedOrder(null); setPaymentAmount(''); }}
                                                className="text-xs text-zinc-400 hover:text-white underline transition-colors flex items-center gap-1"
                                            >
                                                <X size={12} />
                                                Clear selection (Pay custom amount)
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-6">
                                {/* Bank Details Card */}
                                <div className="bg-gradient-to-br from-green-500/10 via-emerald-500/10 to-teal-500/10 border border-green-500/30 p-6 rounded-2xl">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-green-500/20 rounded-lg">
                                            <DollarSign size={20} className="text-green-400" />
                                        </div>
                                        <p className="font-bold text-green-100 text-lg">Bank Transfer Details</p>
                                    </div>
                                    <div className="bg-black/20 backdrop-blur-sm p-4 rounded-xl border border-green-500/20">
                                        <p className="text-xs text-green-300/60 mb-2 uppercase tracking-wide">Instapay Number</p>
                                        <div className="font-mono text-3xl text-green-300 tracking-wider font-bold">
                                            01279347434
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 mt-4">
                                        <p className="text-zinc-400 text-xs flex items-start gap-2 flex-1">
                                            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                                            <span>Transfer the exact amount and upload a screenshot below</span>
                                        </p>
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="bg-green-600/20 hover:bg-green-600/30 border-green-500/30 text-green-400 whitespace-nowrap"
                                            onClick={() => window.open('https://wa.me/201556761102?text=Hello, I need help with my payment.', '_blank')}
                                        >
                                            <MessageCircle size={14} className="mr-1" />
                                            Help
                                        </Button>
                                    </div>
                                </div>

                                {/* Amount Input */}
                                <div>
                                    <label className="block text-sm font-bold text-zinc-300 mb-3">Payment Amount (EGP)</label>
                                    <Input
                                        type="number"
                                        value={paymentAmount}
                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                        placeholder="0.00"
                                        disabled={!!selectedOrder}
                                        className="text-2xl font-mono h-16 bg-zinc-800/50 border-zinc-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                    />
                                    {selectedOrder && (
                                        <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
                                            <AlertCircle size={12} />
                                            Amount is locked for case payment
                                        </p>
                                    )}
                                </div>

                                {/* File Upload */}
                                <div>
                                    <label className="block text-sm font-bold text-zinc-300 mb-3">Upload Receipt</label>
                                    <div className="border-2 border-dashed border-zinc-700 hover:border-blue-500/50 rounded-2xl p-10 text-center transition-all duration-300 relative group bg-zinc-800/30 hover:bg-zinc-800/50">
                                        <input
                                            type="file"
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                                            onChange={(e) => setPaymentFile(e.target.files?.[0] || null)}
                                            accept="image/*,application/pdf"
                                        />
                                        {paymentFile ? (
                                            <div className="text-green-400 flex flex-col items-center justify-center gap-3 font-bold animate-fade-in">
                                                <div className="p-4 bg-green-500/10 rounded-2xl">
                                                    <CheckCircle size={40} />
                                                </div>
                                                <div>
                                                    <p className="text-lg">{paymentFile.name}</p>
                                                    <p className="text-xs text-zinc-500 mt-1">Click to change file</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-zinc-500 flex flex-col items-center gap-3 group-hover:text-blue-400 transition-colors">
                                                <div className="p-4 bg-blue-500/5 group-hover:bg-blue-500/10 rounded-2xl transition-colors">
                                                    <Upload size={40} />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-lg">Click to upload screenshot</p>
                                                    <p className="text-xs mt-1 opacity-70">JPG, PNG or PDF accepted</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <Button
                                    onClick={handlePaymentSubmit}
                                    disabled={loading || !paymentAmount || !paymentFile}
                                    className="w-full h-16 text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-2xl shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Processing...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle size={20} />
                                            Submit Payment Request
                                        </span>
                                    )}
                                </Button>
                            </div>
                        </Card>
                    </div>
                )}

                {/* HISTORY TAB - Enhanced */}
                {activeTab === 'history' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Pending Requests */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-yellow-500/10 rounded-lg">
                                    <Clock size={18} className="text-yellow-400" />
                                </div>
                                <h3 className="font-bold text-white text-lg">Pending Requests</h3>
                            </div>

                            <div className="space-y-3">
                                {myTopUpRequests.map(req => (
                                    <Card key={req.id} className="bg-zinc-900/80 border-zinc-800 p-4 hover:bg-zinc-800/50 transition-all duration-200 shadow-lg">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <div className="text-white font-bold text-lg">{req.amount} EGP</div>
                                                <div className="text-xs text-zinc-500 mt-1">
                                                    {new Date(req.createdAt).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </div>
                                            <StatusBadge status={req.status as any} />
                                        </div>
                                    </Card>
                                ))}
                                {myTopUpRequests.length === 0 && (
                                    <div className="text-zinc-500 text-sm italic text-center py-8">
                                        <Clock size={32} className="mx-auto mb-2 opacity-30" />
                                        No pending requests
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Transaction History */}
                        <div className="lg:col-span-2 space-y-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-blue-500/10 rounded-lg">
                                    <Receipt size={18} className="text-blue-400" />
                                </div>
                                <h3 className="font-bold text-white text-lg">Transaction History & Invoices</h3>
                            </div>

                            <Card className="bg-zinc-900/80 border-zinc-800 shadow-xl overflow-hidden backdrop-blur-sm">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-zinc-800/50 text-zinc-400 text-xs uppercase font-bold">
                                        <tr>
                                            <th className="p-4">Date</th>
                                            <th className="p-4">Description</th>
                                            <th className="p-4 text-right">Amount</th>
                                            <th className="p-4 text-right">Invoice</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-800">
                                        {transactions.map(t => (
                                            <tr key={t.id} className="hover:bg-zinc-800/30 transition-colors">
                                                <td className="p-4 text-zinc-500">
                                                    {new Date(t.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="p-4 text-white">
                                                    <div>
                                                        {t.description}
                                                        {t.type === 'ORDER_PAYMENT' && (
                                                            <span className="block text-xs text-zinc-500 mt-1">Case Payment</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className={`p-4 text-right font-mono font-bold text-lg ${t.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {t.amount > 0 ? '+' : ''}{t.amount}
                                                </td>
                                                <td className="p-4 text-right">
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        className="bg-zinc-800 hover:bg-zinc-700 border-zinc-700"
                                                        onClick={() => setViewInvoice(t)}
                                                    >
                                                        <FileText size={14} className="mr-1" />
                                                        View
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {transactions.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="p-12 text-center text-zinc-500">
                                                    <Receipt size={40} className="mx-auto mb-3 opacity-30" />
                                                    No transaction history available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </Card>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};
